/**
 * LaTeX Parser Utility
 *
 * Parses text containing LaTeX math expressions and splits it into segments.
 * Supports inline math ($...$) and display math ($$...$$).
 */

import React from 'react';

export interface LatexSegment {
    type: 'text' | 'inline-math' | 'display-math';
    content: string;
    key: string;
}

/**
 * Parse text containing LaTeX expressions into segments
 * @param text - Text that may contain LaTeX expressions
 * @returns Array of segments with type and content
 */
export function parseLatex(text: string): LatexSegment[] {
    const segments: LatexSegment[] = [];
    let currentIndex = 0;
    let segmentCounter = 0;

    // Regular expression to match LaTeX expressions
    // Matches $$...$$ (display) or $...$ (inline), but not \$ (escaped)
    const latexRegex = /(?<!\\)\$\$([^\$]+?)\$\$|(?<!\\)\$([^\$]+?)\$/g;

    let match: RegExpExecArray | null;

    while ((match = latexRegex.exec(text)) !== null) {
        const matchStart = match.index;
        const matchEnd = latexRegex.lastIndex;

        // Add plain text before this LaTeX expression
        if (matchStart > currentIndex) {
            const plainText = text.substring(currentIndex, matchStart);
            segments.push({
                type: 'text',
                content: plainText,
                key: `text-${segmentCounter++}`,
            });
        }

        // Determine if it's display or inline math
        const isDisplay = match[0].startsWith('$$');
        const mathContent = isDisplay ? match[1] : match[2];

        segments.push({
            type: isDisplay ? 'display-math' : 'inline-math',
            content: mathContent,
            key: `math-${segmentCounter++}`,
        });

        currentIndex = matchEnd;
    }

    // Add remaining plain text after last LaTeX expression
    if (currentIndex < text.length) {
        const plainText = text.substring(currentIndex);
        segments.push({
            type: 'text',
            content: plainText,
            key: `text-${segmentCounter++}`,
        });
    }

    // If no LaTeX found, return the entire text as one segment
    if (segments.length === 0) {
        segments.push({
            type: 'text',
            content: text,
            key: 'text-0',
        });
    }

    return segments;
}

/**
 * Check if text contains any LaTeX expressions
 * @param text - Text to check
 * @returns True if LaTeX expressions are found
 */
export function containsLatex(text: string): boolean {
    const latexRegex = /(?<!\\)\$\$[^\$]+?\$\$|(?<!\\)\$[^\$]+?\$/;
    return latexRegex.test(text);
}
